import { ApiClient, ScoreRow } from '../../services/ApiClient';
import { GAME_REGISTRY } from '../../engine/GameRegistry';
import { FriendService } from '../../services/FriendService';
import { FriendModal } from '../components/FriendModal';
import { AVATARS_LIST } from '../../core/store/authStore';

export class LeaderboardView {
  private static activeSubTab: 'global' | 'friends' = 'global';
  private static activeGame: string = 'all';
  private static activeLimit: number = 10;
  private static loading = false;

  static render(container: HTMLElement): void {
    // Render skeleton first, then load real data
    this._renderShell(container);
    this._loadAndFill(container);
  }

  private static async _renderShell(container: HTMLElement): Promise<void> {
    const friends = await FriendService.getFriends();

    container.innerHTML = `
      <div style="max-width:950px;margin:30px auto;padding:0 20px;width:100%;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:24px;flex-wrap:wrap;gap:16px;">
          <div>
            <h2 style="font-size:2.2rem;font-weight:800;" class="gradient-text">🏆 Skor Tablosu &amp; Yarışma</h2>
            <p style="color:#94a3b8;margin-top:4px;">Şampiyonların ve arkadaşlarının canlı skor sıralamaları.</p>
          </div>

          <div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;">
            <!-- Game filter -->
            <select id="lb-game-select" style="padding:8px 14px;background:#1e293b;border:1px solid #334155;border-radius:10px;color:#f8fafc;font-size:0.88rem;outline:none;max-width:200px;">
              <option value="all">Tüm Oyunlar</option>
              ${GAME_REGISTRY.map(g => `<option value="${g.id}" ${this.activeGame === g.id ? 'selected' : ''}>${g.title}</option>`).join('')}
            </select>

            <div id="lb-limit-container" style="display:${this.activeSubTab === 'global' ? 'flex' : 'none'};gap:6px;background:rgba(30,41,59,0.8);padding:4px;border-radius:12px;border:1px solid #334155;">
              <button data-limit="10" class="lb-limit-btn" style="padding:6px 12px;border-radius:8px;border:none;cursor:pointer;font-weight:600;font-size:0.85rem;background:${this.activeLimit === 10 ? '#6366f1' : 'transparent'};color:${this.activeLimit === 10 ? '#fff' : '#94a3b8'};transition:all 0.2s;">Top 10</button>
              <button data-limit="20" class="lb-limit-btn" style="padding:6px 12px;border-radius:8px;border:none;cursor:pointer;font-weight:600;font-size:0.85rem;background:${this.activeLimit === 20 ? '#6366f1' : 'transparent'};color:${this.activeLimit === 20 ? '#fff' : '#94a3b8'};transition:all 0.2s;">Top 20</button>
              <button data-limit="50" class="lb-limit-btn" style="padding:6px 12px;border-radius:8px;border:none;cursor:pointer;font-weight:600;font-size:0.85rem;background:${this.activeLimit === 50 ? '#6366f1' : 'transparent'};color:${this.activeLimit === 50 ? '#fff' : '#94a3b8'};transition:all 0.2s;">Top 50</button>
            </div>

            <div style="display:flex;gap:6px;background:rgba(30,41,59,0.8);padding:4px;border-radius:12px;border:1px solid #334155;">
              <button id="lb-global-btn" class="hub-tab-btn ${this.activeSubTab === 'global' ? 'hub-tab-btn--active' : ''}">
                🌐 Global
              </button>
              <button id="lb-friends-btn" class="hub-tab-btn ${this.activeSubTab === 'friends' ? 'hub-tab-btn--active' : ''}">
                👥 Arkadaşlar (${friends.length})
              </button>
            </div>

            <button id="open-friend-modal-btn" style="padding:8px 16px;background:linear-gradient(135deg,#6366f1,#a855f7);color:#fff;border-radius:10px;font-weight:700;font-size:0.88rem;box-shadow:0 4px 12px rgba(99,102,241,0.35);">
              👥 Arkadaş Ekle
            </button>
          </div>
        </div>

        <div class="glass-panel" style="padding:24px;border-radius:16px;min-height:200px;" id="lb-table-wrap">
          <div style="text-align:center;padding:40px;color:#64748b;">
            <div style="font-size:2rem;margin-bottom:12px;animation:spin 1s linear infinite;">⏳</div>
            <div>Skor tablosu yükleniyor...</div>
          </div>
        </div>
      </div>`;

    // Wire tab / filter events immediately
    container.querySelector('#lb-global-btn')?.addEventListener('click', () => {
      this.activeSubTab = 'global';
      this._renderShell(container);
      this._loadAndFill(container);
    });
    container.querySelector('#lb-friends-btn')?.addEventListener('click', () => {
      this.activeSubTab = 'friends';
      this._renderShell(container);
      this._loadAndFill(container);
    });
    container.querySelector('#open-friend-modal-btn')?.addEventListener('click', () => FriendModal.show());
    (container.querySelector('#lb-game-select') as HTMLSelectElement)?.addEventListener('change', (e) => {
      this.activeGame = (e.target as HTMLSelectElement).value;
      this._renderShell(container); // Re-render to update select state
      this._loadAndFill(container);
    });

    container.querySelectorAll('.lb-limit-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        this.activeLimit = parseInt(target.getAttribute('data-limit') || '10', 10);
        this._renderShell(container);
        this._loadAndFill(container);
      });
    });
  }

  private static async _loadAndFill(container: HTMLElement): Promise<void> {
    if (this.loading) return;
    this.loading = true;

    const wrap = container.querySelector('#lb-table-wrap');
    if (!wrap) return;

    let rows: Array<{rank:number;username:string;game_id:string;score:number;avatar_id?:string;achieved_at?:string}> = [];

    if (this.activeSubTab === 'global') {
      const { data, error } = await ApiClient.getTopScores(this.activeGame, this.activeLimit);

      if (error) {
        wrap.innerHTML = `<div style="text-align:center;padding:32px;color:#ef4444;">
          ⚠️ Sunucuya bağlanılamadı.<br>
          <span style="font-size:0.8rem;color:#64748b;">Docker çalışıyor mu? <code>docker-compose up -d</code></span>
        </div>`;
        this.loading = false;
        return;
      }

      rows = (data || []).map((r: ScoreRow, idx: number) => ({
        rank: idx + 1,
        username: (r as ScoreRow & {username?: string}).username || 'Oyuncu',
        game_id: r.game_id,
        score: r.score,
        avatar_id: (r as ScoreRow & {avatar_id?: string}).avatar_id,
        achieved_at: r.achieved_at,
      }));
    } else {
      // Friends tab: show friend scores from local friend service
      const friends = await FriendService.getFriends();
      rows = friends
        .map((f, idx) => ({
          rank: idx + 1,
          username: f.username,
          game_id: f.bestGame || 'tetris',
          score: f.topScore,
          avatar_id: undefined,
          achieved_at: f.status === 'online' || f.status === 'in_game' ? 'Şu an Aktif' : 'Dün',
        }))
        .sort((a, b) => b.score - a.score)
        .map((item, idx) => ({ ...item, rank: idx + 1 }));
    }

    if (rows.length === 0) {
      wrap.innerHTML = `<div style="text-align:center;padding:40px;color:#64748b;">
        <div style="font-size:2.5rem;margin-bottom:8px;">🏁</div>
        <div>Henüz skor kaydedilmemiş.</div>
        <div style="font-size:0.82rem;margin-top:4px;">İlk yerini kap!</div>
      </div>`;
      this.loading = false;
      return;
    }

    wrap.innerHTML = `
      <table style="width:100%;border-collapse:collapse;text-align:left;">
        <thead>
          <tr style="border-bottom:1px solid #334155;color:#94a3b8;font-size:0.85rem;text-transform:uppercase;">
            <th style="padding:12px 16px;"># Sıra</th>
            <th style="padding:12px 16px;">Oyuncu</th>
            <th style="padding:12px 16px;">Oyun</th>
            <th style="padding:12px 16px;text-align:right;">Skor</th>
            <th style="padding:12px 16px;text-align:right;">Tarih</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map((s) => {
            const avatar = AVATARS_LIST.find(a => a.id === s.avatar_id);
            const icon   = avatar?.icon ?? '🎮';
            const dateStr = s.achieved_at
              ? (typeof s.achieved_at === 'string' && s.achieved_at.length < 15
                ? s.achieved_at
                : new Date(s.achieved_at).toLocaleDateString('tr-TR'))
              : '—';
            return `
            <tr style="border-bottom:1px solid rgba(255,255,255,0.05);font-size:0.95rem;transition:background 0.2s;"
                onmouseover="this.style.background='rgba(255,255,255,0.03)'"
                onmouseout="this.style.background='transparent'">
              <td style="padding:16px;font-weight:700;">
                ${s.rank === 1 ? '🥇' : s.rank === 2 ? '🥈' : s.rank === 3 ? '🥉' : `<span style="color:#64748b;">#${s.rank}</span>`}
              </td>
              <td style="padding:16px;font-weight:600;color:#f8fafc;display:flex;align-items:center;gap:8px;">
                <span style="font-size:1.1rem;">${icon}</span> ${s.username}
              </td>
              <td style="padding:16px;color:#818cf8;">
                <span style="background:rgba(99,102,241,0.15);padding:4px 10px;border-radius:12px;font-size:0.8rem;font-weight:700;">${s.game_id}</span>
              </td>
              <td style="padding:16px;text-align:right;font-weight:800;color:#38bdf8;">${s.score.toLocaleString()}</td>
              <td style="padding:16px;text-align:right;color:#64748b;font-size:0.85rem;">${dateStr}</td>
            </tr>`;
          }).join('')}
        </tbody>
      </table>`;

    this.loading = false;
  }
}
